import React, {FC, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import ScrollMenu from 'react-horizontal-scrolling-menu';
import {MenuItems} from 'react-horizontal-scrolling-menu/build/types';

import {DateTab, SearchTerm} from 'utils/types';
import arrow from 'assets/svg/matchcenter_tabs_arrow.svg';
import useBreakpointLG from 'hooks/useBreakpointLG';
import CustomScrollMenu from 'components/Common/CustomScrollMenu';
import './styles.scss';

interface Props {
  searchTerm: SearchTerm;
  dateTabs: DateTab[];
  tab: number;
  setTab: (index: number) => void;
}

const DateTabs: FC<Props> = ({searchTerm, dateTabs, tab, setTab}: Props) => {
  const tabsRef = useRef<CustomScrollMenu | null>(null);
  const isBreakpointLG = useBreakpointLG();

  const tabs = useMemo(() => {
    const arrows = [
      <button className="DateTabs__arrow DateTabs__arrow--start" type="button" key="arrow-start">
        <img src={arrow} alt="arrow" />
      </button>,
      <button className="DateTabs__arrow DateTabs__arrow--end" type="button" key="arrow-end">
        <img src={arrow} alt="arrow" />
      </button>,
    ];
    const dates = dateTabs.map(d => (
      <button className="DateTabs__tab" type="button" key={d.date}>
        <span className={`DateTabs__label ${searchTerm ? '' : 'DateTabs__label--small'}`}>
          {d.labelTop}
        </span>
        <span className={`DateTabs__label ${searchTerm ? 'DateTabs__label--small' : ''}`}>
          {d.labelBottom}
        </span>
      </button>
    ));

    arrows.splice(1, 0, ...dates);
    return arrows;
  }, [searchTerm, dateTabs]);

  useEffect(() => {
    if (tabsRef.current) {
      tabsRef.current.scrollTo(`${tabs[tab + 1].key}`);
    }
  }, [tabsRef, tabs, tab]);

  return (
    <CustomScrollMenu
      ref={tabsRef}
      data={tabs}
      arrowLeft={
        isBreakpointLG ? (
          <div className="DateTabs__desktopArrow DateTabs__desktopArrow--left" />
        ) : null
      }
      arrowRight={
        isBreakpointLG ? (
          <div className="DateTabs__desktopArrow DateTabs__desktopArrow--right" />
        ) : null
      }
      alignCenter
      alignOnResize={false}
      inertiaScrolling
      wheel={false}
      menuClass="DateTabs"
      selected={`${tabs[tab + 1].key}`} // +1 to account for arrow tab at start
      translate={
        /* FIXME: fix this mess with proper detection of intially selected item */
        -1 * (tabs.length / 2) * (isBreakpointLG ? 90 : 80) +
        (isBreakpointLG ? 354 : window.innerWidth / 2)
      }
      onSelect={key => {
        if (!key || !tabsRef.current) return;

        const index = tabsRef.current.getItemIndexByKey(`${key}`);
        if (index < 0) return;

        const items = tabsRef.current.getMenuItems();

        if (index === 0 || index === items.length - 1) {
          // clicked arrow at either end - go to middle index
          const middleIndex = Math.ceil((items.length - 2) / 2);
          tabsRef.current.onItemClick(items[middleIndex][1].key);
        } else {
          // remove first arrow item from indexing
          const dateIndex = index - 1;
          setTab(dateIndex);
        }
      }}
    />
  );
};

export default React.memo(DateTabs);
